import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { UserRound, Briefcase, Check, ChevronDown, X } from 'lucide-react';

/**
 * Combined picker sourcing from BOTH platform users and workforce employees.
 * Each option has a kind icon (user = UserRound, employee = Briefcase).
 *
 * Props:
 *  - pool: { users: [{kind,id,name,sub}], employees: [{kind,id,name,sub}] }
 *  - selectedUserIds: string[]
 *  - selectedEmployeeIds: string[]
 *  - onUsersChange(ids), onEmployeesChange(ids)
 *  - multi: true (default) — when false, acts as single-select + onSingleChange({kind,id})
 *  - singleValue: {kind, id} | null  (only used when multi=false)
 *  - onSingleChange({kind, id} | null)
 *  - placeholder
 *  - testIdPrefix
 */
export const PeoplePicker = ({
  pool = { users: [], employees: [] },
  selectedUserIds = [],
  selectedEmployeeIds = [],
  onUsersChange,
  onEmployeesChange,
  multi = true,
  singleValue = null,
  onSingleChange,
  placeholder = 'Select people...',
  testIdPrefix = 'people-picker',
  disabledKey, // {kind, id} that cannot be unselected (e.g. responsible lock)
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const users = pool.users || [];
  const employees = pool.employees || [];

  const userMap = useMemo(() => Object.fromEntries(users.map(u => [u.id, u])), [users]);
  const empMap = useMemo(() => Object.fromEntries(employees.map(e => [e.id, e])), [employees]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const match = (p) => !q || p.name.toLowerCase().includes(q) || (p.sub || '').toLowerCase().includes(q);
    return { users: users.filter(match), employees: employees.filter(match) };
  }, [users, employees, query]);

  const isSelected = (kind, id) => {
    if (multi) {
      return kind === 'user' ? selectedUserIds.includes(id) : selectedEmployeeIds.includes(id);
    }
    return singleValue && singleValue.kind === kind && singleValue.id === id;
  };

  const isDisabled = (kind, id) => disabledKey && disabledKey.kind === kind && disabledKey.id === id;

  const toggle = (kind, id) => {
    if (multi) {
      if (kind === 'user') {
        if (isDisabled(kind, id) && selectedUserIds.includes(id)) return;
        onUsersChange(selectedUserIds.includes(id) ? selectedUserIds.filter(x => x !== id) : [...selectedUserIds, id]);
      } else {
        if (isDisabled(kind, id) && selectedEmployeeIds.includes(id)) return;
        onEmployeesChange(selectedEmployeeIds.includes(id) ? selectedEmployeeIds.filter(x => x !== id) : [...selectedEmployeeIds, id]);
      }
    } else {
      onSingleChange({ kind, id });
      setOpen(false);
    }
  };

  // Trigger label
  let triggerLabel = placeholder;
  if (!multi && singleValue) {
    const p = singleValue.kind === 'user' ? userMap[singleValue.id] : empMap[singleValue.id];
    triggerLabel = p ? p.name : placeholder;
  } else if (multi) {
    const total = selectedUserIds.length + selectedEmployeeIds.length;
    if (total > 0) triggerLabel = `${total} selected`;
  }

  const selectedUserChips = multi ? selectedUserIds.map(id => userMap[id]).filter(Boolean) : [];
  const selectedEmpChips = multi ? selectedEmployeeIds.map(id => empMap[id]).filter(Boolean) : [];

  const renderRow = (p) => {
    const selected = isSelected(p.kind, p.id);
    const disabled = isDisabled(p.kind, p.id);
    const Icon = p.kind === 'user' ? UserRound : Briefcase;
    return (
      <button
        key={`${p.kind}-${p.id}`}
        type="button"
        onClick={() => toggle(p.kind, p.id)}
        className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm text-left transition-colors ${selected ? 'bg-primary/10' : 'hover:bg-muted/60'} ${disabled ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'}`}
        data-testid={`${testIdPrefix}-${p.kind}-${p.id}`}
      >
        {multi ? (
          <Checkbox checked={selected} disabled={disabled} className="pointer-events-none" />
        ) : (
          <span className="w-4 h-4 flex items-center justify-center">
            {selected && <Check className="h-3.5 w-3.5 text-primary" />}
          </span>
        )}
        <Icon className={`h-3.5 w-3.5 flex-shrink-0 ${p.kind === 'user' ? 'text-[#2D8C8C]' : 'text-amber-500'}`} />
        <span className="flex-1 truncate">{p.name}</span>
        {p.sub && <span className="text-xs text-muted-foreground truncate max-w-[40%]">{p.sub}</span>}
      </button>
    );
  };

  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            className="w-full justify-between font-normal"
            data-testid={`${testIdPrefix}-trigger`}
          >
            <span className="truncate text-left flex items-center gap-2">
              {!multi && singleValue && (
                singleValue.kind === 'user'
                  ? <UserRound className="h-3.5 w-3.5 text-[#2D8C8C]" />
                  : <Briefcase className="h-3.5 w-3.5 text-amber-500" />
              )}
              {triggerLabel}
            </span>
            <ChevronDown className="h-4 w-4 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[360px] p-2" align="start">
          <Input
            autoFocus
            placeholder="Search by name, UZK, dept..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="h-8 mb-2"
            data-testid={`${testIdPrefix}-search`}
          />
          <div className="max-h-72 overflow-y-auto space-y-0.5">
            {filtered.employees.length > 0 && (
              <>
                <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground px-2 pt-1 pb-0.5 flex items-center gap-1">
                  <Briefcase className="h-3 w-3" /> Workforce employees
                </p>
                {filtered.employees.map(renderRow)}
              </>
            )}
            {filtered.users.length > 0 && (
              <>
                <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground px-2 pt-2 pb-0.5 flex items-center gap-1">
                  <UserRound className="h-3 w-3" /> Platform users
                </p>
                {filtered.users.map(renderRow)}
              </>
            )}
            {filtered.users.length === 0 && filtered.employees.length === 0 && (
              <p className="text-xs text-muted-foreground text-center py-6">No matches</p>
            )}
          </div>
          {multi && (selectedUserIds.length + selectedEmployeeIds.length) > 0 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-full mt-2 text-xs"
              onClick={() => { onUsersChange([]); onEmployeesChange([]); }}
            >
              Clear selection
            </Button>
          )}
        </PopoverContent>
      </Popover>

      {multi && (selectedUserChips.length > 0 || selectedEmpChips.length > 0) && (
        <div className="flex flex-wrap gap-1.5" data-testid={`${testIdPrefix}-chips`}>
          {selectedEmpChips.map(e => (
            <span key={`emp-${e.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-300 text-xs border border-amber-500/20">
              <Briefcase className="h-3 w-3" />
              {e.name}
              {!isDisabled('employee', e.id) && (
                <button type="button" onClick={() => toggle('employee', e.id)} className="ml-0.5 hover:text-rose-500">
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          ))}
          {selectedUserChips.map(u => (
            <span key={`usr-${u.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#2D8C8C]/10 text-[#2D8C8C] text-xs border border-[#2D8C8C]/20">
              <UserRound className="h-3 w-3" />
              {u.name}
              {!isDisabled('user', u.id) && (
                <button type="button" onClick={() => toggle('user', u.id)} className="ml-0.5 hover:text-rose-500">
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default PeoplePicker;
